<script setup>
import { computed, ref } from 'vue';
import { __, sortDates } from '@/utils'
import Modal from "@components/modal.vue";
import Flex from '@components/flex/flex.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useDeviceType } from '@composables/useDeviceType';

const { deviceType } = useDeviceType();

const props = defineProps({
    active: Boolean,
    date: {
        type: Array,
        default: () => {
            const currentDate = new Date();
            let startDate = new Date(currentDate.getFullYear(), currentDate.getMonth()-1, 1);
            let endDate = currentDate;

            return [startDate, endDate];
        }
    },
    title : {
        type: String,
        default: __('Updating the range of dates for the activation of an overseas package')
    },
    subtext : {
        type: String,
        default: __('Please select a date range to activate the package')
    },
});

const multiCalendars = computed(() => {
    if(deviceType.value === 'mobile') {
        return false
    } 

    return {
        solo: true
    }
});

const emit = defineEmits(['close', 'accept']);

const date = ref(props.date);
const datepicker = ref();
const tempDateStart = ref(null);
const tempDateEnd = ref(null);

const daysOfWeek = [
    __('su'),
    __('m'),
    __('tu'),
    __('w'),
    __('th'),
    __('f'),
    __('sa'),
];

const formatDate = (value) => {
    if (!value) {
        return '';
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toLocaleDateString('en-GB');
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
            return trimmed;
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            const [year, month, day] = trimmed.split('-');
            return `${day}/${month}/${year}`;
        }

        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed.toLocaleDateString('en-GB');
        }
        return trimmed;
    }

    return '';
};

const selectedDates = computed(() => {
    if (date.value.length !== 2 || date.value.includes(null)) {
        return [];
    }
    return date.value.map(item => formatDate(item));
});

const displayStartDate = computed(() => formatDate(tempDateStart.value || selectedDates.value[0]));
const displayEndDate = computed(() => formatDate(tempDateEnd.value || selectedDates.value[1]));

const resetTempFields = () => {
    tempDateStart.value = null;
    tempDateEnd.value = null;
}

const handleAccept = () => {
    datepicker.value.selectDate();
    resetTempFields();

    if (date.value.length === 2 && !date.value.includes(null)) {
        date.value.sort((a, b) => a - b);
        emit('accept', date.value);
    }
}

const handleRangeStart = (range) => {
    tempDateStart.value = range.toLocaleDateString("en-GB");
}
const handleRangeEnd = (value) => {
    const startRange = new Date(tempDateStart.value.split('/').reverse().join('-'));
    const [from, to] = sortDates(startRange, value);
 
    tempDateStart.value = from.toLocaleDateString("en-GB");
    tempDateEnd.value = to.toLocaleDateString("en-GB");
};

const handleClose = () => {
    emit('close');
    resetTempFields();
}

</script>
<template>
    <Modal :isActive="active" @close="handleClose" :class="['larger calendar-holder', {
                'calendar-holder--mobile': !multiCalendars?.solo
        }]">
        <template #head>
            <h3>{{ title }}</h3>
        </template>
        <template #body>
            <p class="calendar-header" >{{ subtext }}</p>
            <Flex v-if="multiCalendars?.solo" align="center" justify="center" class="date-fields">
                <div class="date-fields__field">
                    <label>{{ __('from') }}</label>
                    <div class="date-field-input-holder">
                        <input type="text" :value="displayStartDate" readonly />
                        <img :src="`${theme_path.url}/resources/images/svg/icon-calendar-gradient.svg`" />
                    </div>
                </div>
                <div class="line-devider"></div>
                <div class="date-fields__field">
                    <label>{{ __('to') }}</label>
                    <div class="date-field-input-holder">
                        <input type="text" :value="displayEndDate" readonly />
                        <img :src="`${theme_path.url}/resources/images/svg/icon-calendar-gradient.svg`" />
                    </div>
                </div>
            </Flex>
            <div :class="['calendar', {'calendar--larger': !multiCalendars?.solo}]">
                <VueDatePicker
                    ref="datepicker"
                    inline
                    locale="he"
                    :week-start="0"
                    range
                    @range-start="handleRangeStart"
                    @range-end="handleRangeEnd"
                    :multi-calendars="multiCalendars"
                    :day-names="daysOfWeek"
                    :clearable="false"
                    :hide-navigation="['time', 'minutes', 'hours', 'seconds']"
                    :action-row="{ showSelect: false, showPreview: false }"
                    :max-date="new Date()"
                    v-model="date" 
                >
                    <template #arrow-left>
                        <img class="slot-icon" :src="`${theme_path.url}/resources/images/svg/icon-arrow-left-gradient.svg`" />
                    </template>
                    <template #arrow-right>
                        <img class="slot-icon slot-icon--reversed" :src="`${theme_path.url}/resources/images/svg/icon-arrow-left-gradient.svg`" />
                    </template>
            </VueDatePicker>
            </div><!-- /.calendar -->
            <Flex :gap="27" align="center" justify="flex-end" class="calendar-holder__actions" >
                <button class="btn--transparent link-underline" @click="$emit('close')">{{ __('cancelation') }}</button>
                <button class="btn btn--save" @click="handleAccept">{{ __('updating') }}</button>
            </Flex>
        </template>
    </Modal>
</template>